import { Group, IconButton, iconSizes, Paper, Stack, Switch, Text } from '@common/ui'
import TrimmedTextInput from './TrimmedTextInput'
import { IconPlus, IconTrash } from '@common/ui/icons'
import { com, kotlin } from '@eidu/web'
import ItemList from './ItemList'
import { keyOf, withKey } from '../../util/withKey'
import FormKeyField from './FormKeyField'
import MultipleChoice = com.eidu.sharedlib.form.definition.MultipleChoice
import MultipleChoiceOption = com.eidu.sharedlib.form.definition.MultipleChoiceOption
import Item = com.eidu.sharedlib.form.definition.Item
import KtList = kotlin.collections.KtList

type MultipleChoiceItemProps = {
  item: MultipleChoice
  onChange?: (item: MultipleChoice) => void
}

const MultipleChoiceItem: React.FC<MultipleChoiceItemProps> = ({ item, onChange }) => {
  const handleOptionChange = (index: number, key: string, text: string) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    newOptions[index] = withKey(new MultipleChoiceOption(key, text, newOptions[index].items), newOptions[index])
    onChange?.(withKey(item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.required), item))
  }

  const handleOptionItemsChange = (index: number, newItems: KtList<Item>) => {
    const newOptions = [...item.options.asJsReadonlyArrayView()]
    newOptions[index] = withKey(
      new MultipleChoiceOption(newOptions[index].key, newOptions[index].text, newItems),
      newOptions[index]
    )
    onChange?.(withKey(item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.required), item))
  }

  const addOption = () => {
    const newOptions = [
      ...item.options.asJsReadonlyArrayView(),
      new MultipleChoiceOption(`option_${Date.now()}`, 'New option', KtList.fromJsArray([])),
    ]
    onChange?.(withKey(item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.required), item))
  }

  const removeOption = (index: number) => {
    const newOptions = item.options.asJsReadonlyArrayView().filter((_, i) => i !== index)
    onChange?.(withKey(item.copy(item.prompt, KtList.fromJsArray(newOptions), item.key, item.required), item))
  }

  return (
    <Paper withBorder radius="sm" p="xs">
      <Stack gap="md">
        <Group gap="md" wrap="nowrap" align="flex-start">
          <TrimmedTextInput
            style={{ flex: 1 }}
            label="Prompt"
            value={item.prompt}
            onChange={(e) =>
              onChange?.(withKey(item.copy(e.target.value, item.options, item.key, item.required), item))
            }
            disabled={!onChange}
          />
          <FormKeyField
            value={item.key}
            onChange={(e) =>
              onChange?.(withKey(item.copy(item.prompt, item.options, e.target.value, item.required), item))
            }
            disabled={!onChange}
          />
          <Switch
            checked={item.required}
            onChange={(e) =>
              onChange?.(withKey(item.copy(item.prompt, item.options, item.key, e.target.checked), item))
            }
            disabled={!onChange}
            label="Required"
          />
        </Group>
        <Stack gap="md">
          <Text size="sm" fw={600}>
            Options
          </Text>
          {item.options.asJsReadonlyArrayView().map((option, index) => (
            <Paper withBorder radius="sm" p="xs" key={keyOf(option)}>
              <Stack gap="xs">
                <Group gap="md" wrap="nowrap">
                  <TrimmedTextInput
                    style={{ flex: 1 }}
                    label={`Option ${index + 1}`}
                    value={option.text}
                    onChange={(e) => handleOptionChange(index, option.key, e.target.value)}
                    disabled={!onChange}
                  />
                  <FormKeyField
                    label="Key"
                    value={option.key}
                    onChange={(e) => handleOptionChange(index, e.target.value, option.text)}
                    disabled={!onChange}
                  />
                  {onChange && (
                    <IconButton size="sm" aria-label="Remove option" onClick={() => removeOption(index)}>
                      <IconTrash size={iconSizes.sm} stroke={1.5} />
                    </IconButton>
                  )}
                </Group>
                <Paper radius="sm" p="xs" bg="var(--trellis-color-surface)">
                  <ItemList
                    items={option.items}
                    onChange={onChange && ((newItems) => handleOptionItemsChange(index, newItems))}
                  />
                </Paper>
              </Stack>
            </Paper>
          ))}
          {onChange && (
            <IconButton size="sm" aria-label="Add option" onClick={addOption} style={{ alignSelf: 'center' }}>
              <IconPlus size={iconSizes.sm} stroke={1.5} />
            </IconButton>
          )}
        </Stack>
      </Stack>
    </Paper>
  )
}

export default MultipleChoiceItem
